var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { FilterType, } from "@/__generated__/graphql";
import { GlobalContext } from "@/contexts";
import { responseToSearchData, FIND_JOBS_QUERY, } from "@/graphql/queries/FindJobs";
import { useCountryParam } from "@/hooks/useCountryParam";
import { getJobCategorySuids } from "@/jobCategorySuids";
import { UserTrackingFlow } from "@/types/ApplyFlowParameters";
import { isJobDatePostedValue } from "@/types/SearchData";
import { SearchParamType } from "@/types/SearchParamType";
import { useApolloClient, useQuery } from "@apollo/client";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
export var useSearchResults = function () {
    var _a = useContext(GlobalContext), applyFlowParameters = _a.applyFlowParameters, properties = _a.properties;
    var searchParams = useSearchParams()[0];
    var countryParam = useCountryParam();
    var _b = useState(countryParam), currentCountry = _b[0], setCountry = _b[1];
    var client = useApolloClient();
    var isInitialLoadRef = useRef(true);
    var filterOptionsCountryRef = useRef();
    useEffect(function () {
        if (currentCountry !== countryParam && countryParam) {
            setCountry(countryParam);
            client.resetStore();
        }
    }, [countryParam, currentCountry, client]);
    var variables = useMemo(function () {
        var vars = {
            isCareerSite: (applyFlowParameters === null || applyFlowParameters === void 0 ? void 0 : applyFlowParameters.userTrackingFlow) === UserTrackingFlow.CAREER_SITES,
            includeDatePostedFilterOptions: properties.modules.datePostedFilter === true,
            includeRemoteWorkTypeFilterOptions: properties.modules.remoteOnlyToggle === true,
            includeJobCategoryFilterOptions: properties.modules.occupationsFilter === true,
            currentUrl: window.location.href,
        };
        var cursor = searchParams.get(SearchParamType.CURSOR);
        if (cursor) {
            vars.after = cursor;
        }
        var what = searchParams.get(SearchParamType.WHAT);
        if (what) {
            vars.what = what;
        }
        var where = searchParams.get(SearchParamType.WHERE);
        if (where) {
            vars.where = where;
        }
        var isRemoteOnly = searchParams.get(SearchParamType.IS_REMOTE_ONLY);
        if (isRemoteOnly) {
            vars.remoteOnly = isRemoteOnly === "true";
        }
        var filters = [];
        var company = searchParams.get(SearchParamType.COMPANY);
        if (company) {
            filters.push({
                key: FilterType.Company,
                value: company,
            });
        }
        var occupation = searchParams.get(SearchParamType.OCCUPATION);
        if (occupation) {
            filters.push({
                key: FilterType.Occupation,
                value: occupation,
            });
        }
        if (filters.length > 0) {
            vars.filters = filters;
        }
        var jobFilters = {};
        var datePosted = searchParams.get(SearchParamType.DATE_POSTED);
        if (datePosted && isJobDatePostedValue(datePosted)) {
            jobFilters.datePosted = datePosted;
        }
        var jobCategorySuids = getJobCategorySuids(searchParams);
        if (jobCategorySuids.length > 0) {
            jobFilters.jobCategorySuids = jobCategorySuids;
        }
        if (Object.keys(jobFilters).length > 0) {
            vars.jobFilters = jobFilters;
        }
        // Only pass jobKey on initial page load, not on subsequent searches
        if (isInitialLoadRef.current) {
            var jobKey = searchParams.get(SearchParamType.JOB_KEY);
            if (jobKey) {
                vars.jobKey = jobKey;
            }
        }
        return vars;
    }, [
        searchParams,
        applyFlowParameters === null || applyFlowParameters === void 0 ? void 0 : applyFlowParameters.userTrackingFlow,
        properties.modules.datePostedFilter,
        properties.modules.remoteOnlyToggle,
        properties.modules.occupationsFilter,
    ]);
    var headers = useMemo(function () {
        return countryParam ? { "indeed-co": countryParam } : {};
    }, [countryParam]);
    var _c = useQuery(FIND_JOBS_QUERY, {
        variables: variables,
        context: { headers: headers },
    }), loading = _c.loading, error = _c.error, data = _c.data;
    var searchData = useMemo(function () {
        var rawSearchData = data ? responseToSearchData(data) : null;
        if (typeof utl !== "undefined" && utl) {
            utl.resetTrackedJobs();
        }
        // Only reorder jobs on initial page load if there's a selected job key in URL
        if (rawSearchData && isInitialLoadRef.current) {
            var selectedJobKey_1 = searchParams.get(SearchParamType.JOB_KEY);
            if (selectedJobKey_1 && rawSearchData.jobs.length > 0) {
                var selectedJobIndex = rawSearchData.jobs.findIndex(function (job) { return job.jobKey === selectedJobKey_1; });
                if (selectedJobIndex > 0) {
                    // Move selected job to the front
                    var reorderedJobs = __spreadArray([], rawSearchData.jobs, true);
                    var selectedJob = reorderedJobs.splice(selectedJobIndex, 1)[0];
                    reorderedJobs.unshift(selectedJob);
                    return __assign(__assign({}, rawSearchData), { jobs: reorderedJobs });
                }
            }
        }
        return rawSearchData;
    }, [data, searchParams]);
    // Mark initial load as complete after first render
    useEffect(function () {
        if (isInitialLoadRef.current && !loading) {
            isInitialLoadRef.current = false;
        }
    }, [loading]);
    useEffect(function () {
        var _a;
        if (!loading && ((_a = data === null || data === void 0 ? void 0 : data.findJobs) === null || _a === void 0 ? void 0 : _a.filterOptions)) {
            filterOptionsCountryRef.current = countryParam;
        }
    }, [countryParam, data, loading]);
    return {
        loading: loading,
        filterOptionsLoading: loading && filterOptionsCountryRef.current !== countryParam,
        error: error,
        searchData: searchData,
    };
};
